import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Button } from 'reactstrap';
import { useFormContext } from 'react-hook-form';

import {
  guidelineDeliveryTypeOptions,
  guidelineTypeOptions,
  monthNumberLabel,
  betaLanguageOptions,
} from '../../../../constants';
import { ContentCardLayout } from './styles';
import { TrashIcon } from 'assets/icons';
import SelectInput from 'components/Select-Input';
import SpinnerInput from 'components/Spinner-Input';
import TextInput from 'components/Text-Input';
import DisplayErrors from 'components/Display-Errors';
import TableUnifiedLinks from '../Table-Unified-Links';

const currentMonth = new Date().getMonth() + 1;
const currentYear = new Date().getFullYear();
const nextYear = currentYear + 1;
const keywordsLimit = 30;

const yearOptions = [
  { label: currentYear.toString(), value: currentYear },
  { label: nextYear.toString(), value: nextYear },
];

const ContentCard = ({ index, canDelete, onDelete }) => {
  const { t: translate } = useTranslation();
  const {
    formState: { errors },
    clearErrors,
    watch,
    setValue,
    setError,
  } = useFormContext();
  const [selectedMonth, setSelectedMonth] = useState(
    monthNumberLabel.find((month) => month.value === currentMonth),
  );
  const [selectedYear, setSelectedYear] = useState(yearOptions[0]);
  const [isReviewLinkRequired, setIsReviewLinkRequired] = useState(false);

  const unificationLinks = watch(`guidelines.${index}.unificationLinks`) || [];
  const unificationLink = watch(`guidelines.${index}.unificationLink`);

  const handleSelectMonth = (value) => {
    const month = monthNumberLabel.find((month) => month.value === value);
    setSelectedMonth(month);
  };

  const handleSelectYear = (value) => {
    const year = yearOptions.find((year) => year.value === value);
    setSelectedYear(year);

    if (year) {
      setValue(`guidelines.${index}.productionDate.year`, year, { shouldValidate: true });
    } else {
      setValue(`guidelines.${index}.productionDate.year`, null, { shouldValidate: true });
    }
  };

  const handleErrors = (errors) => {
    if (!errors) return {};

    if (errors.type === 'required') {
      return { required: translate('Required Field!') };
    }

    if (errors.type === 'pattern') {
      return { invalidUrl: translate('Invalid URL!') };
    }

    if (errors.message) {
      return { custom: translate(errors.message) };
    }

    return _.isString(errors) ? { custom: translate(errors) } : errors;
  };

  const validateTotalSize = (value) => {
    if (value < 50) {
      return 'Must be greater than or equal to 50';
    }

    if (value >= 50) {
      clearErrors(`guidelines.${index}.totalSize`);
    }

    return true;
  };

  const validateSecondaryKeywordsLimit = (value) => {
    if (!value) return true;

    const keywordsArray = value
      .split(',')
      .map((kw) => kw.trim())
      .filter((kw) => kw);
    if (keywordsArray.length > keywordsLimit) {
      return translate('Secondary Keywords can only contain up to {{count}} keywords', {
        count: keywordsLimit,
      });
    }

    return true;
  };

  const getSelectErrorStyle = (hasError) => ({
    control: (provided) => ({
      ...provided,
      borderColor: hasError ? '#e53e3e' : '#cccccc',
      borderWidth: hasError ? '2px' : '1px',
      '&:hover': {
        borderColor: hasError ? '#e53e3e' : '#bfbfbf',
      },
    }),
  });

  const getInputErrorStyle = (hasError) => ({
    borderColor: hasError ? '#e53e3e' : '#ced4da',
    borderWidth: hasError ? '2px' : '1px',
  });

  const handleAddUnificationLink = async () => {
    const trimmed = unificationLink?.trim();

    const isValidUrl = /^(https?:\/\/)?([\w\-]+\.)+[\w]{2,}(\/\S*)?$/.test(trimmed);

    if (!trimmed || !isValidUrl) {
      setError(`guidelines.${index}.unificationLink`, {
        type: 'pattern',
        message: 'Invalid URL!',
      });
      return;
    }

    if (!unificationLinks.includes(trimmed)) {
      const updated = [...unificationLinks, trimmed];
      setValue(`guidelines.${index}.unificationLinks`, updated, { shouldValidate: true });
      setValue(`guidelines.${index}.unificationLink`, '');
      clearErrors(`guidelines.${index}.unificationLink`);
    }
  };

  const handleRemoveUnificationLink = (urlToRemove) => {
    const updated = unificationLinks.filter((link) => link !== urlToRemove);
    setValue(`guidelines.${index}.unificationLinks`, updated, { shouldValidate: true });
  };

  const handleClearAllUnificationLinks = () => {
    setValue(`guidelines.${index}.unificationLinks`, [], { shouldValidate: true });
  };

  useEffect(() => {
    const deliveryType = watch(`guidelines.${index}.deliveryType.value`);

    setIsReviewLinkRequired(deliveryType === 'REVIEW' || deliveryType === 'REVIEW_AND_DIRECTIONS');
  }, [watch(`guidelines.${index}.deliveryType`)]);

  useEffect(() => {
    const predefinedProductionMonth = watch(`guidelines.${index}.productionDate.month`);
    const predefinedProductionYear = watch(`guidelines.${index}.productionDate.year`);

    // Month Handling
    if (typeof predefinedProductionMonth !== 'object') {
      // Value from CSV or raw string
      const numericMonth = Number(predefinedProductionMonth);
      const month = monthNumberLabel.find((m) => m.value === numericMonth);
      setSelectedMonth(month || null);
    } else {
      setSelectedMonth(predefinedProductionMonth);
    }

    // Year Handling
    if (typeof predefinedProductionYear === 'object') {
      if (predefinedProductionYear?.value) {
        // Valid value from CSV or raw string
        const year = yearOptions.find((y) => y.value === predefinedProductionYear.value);
        setSelectedYear(year);
      } else {
        // Invalid value from CSV
        setSelectedYear(null);
        setValue(`guidelines.${index}.productionDate.year`, null, {
          shouldValidate: true,
        });
      }
    } else if (predefinedProductionYear) {
      // Raw value from defaultValues
      const numericYear = Number(predefinedProductionYear);
      const year = yearOptions.find((y) => y.value === numericYear);
      setSelectedYear(year);
      setValue(`guidelines.${index}.productionDate.year`, year, {
        shouldValidate: true,
      });
    } else {
      // Manual creation without value
      const currentYear = new Date().getFullYear();
      const year = yearOptions.find((y) => y.value === currentYear);
      setSelectedYear(year);
      setValue(`guidelines.${index}.productionDate.year`, year, {
        shouldValidate: true,
      });
    }
  }, [
    watch(`guidelines.${index}.productionDate.month`),
    watch(`guidelines.${index}.productionDate.year`),
  ]);

  return (
    <ContentCardLayout>
      <div className="card-form">
        <div className="d-flex w-100" style={{ width: '100%', justifyContent: 'space-between' }}>
          <div className="d-flex flex-column" style={{ minWidth: '40%' }}>
            <SelectInput
              label="Type"
              controlName={`guidelines.${index}.deliveryType`}
              size={3}
              required
              options={guidelineDeliveryTypeOptions}
              minWidth="100%"
              styles={getSelectErrorStyle(!!errors?.guidelines?.[index]?.deliveryType)}
            />
            <DisplayErrors error={handleErrors(errors?.guidelines?.[index]?.deliveryType)} />
          </div>
          <div className="d-flex flex-column" style={{ minWidth: '56%' }}>
            <TextInput
              type="text"
              label="URL"
              controlName={`guidelines.${index}.reviewLink`}
              size={4}
              placeholder="Insert the link to the content to be reviewed"
              noEllipsis
              required={isReviewLinkRequired}
              minWidth="100%"
              style={{
                ...getInputErrorStyle(!!errors?.guidelines?.[index]?.reviewLink),
              }}
              validation={{
                ...(isReviewLinkRequired && { required: 'Required Field!' }),
                pattern: {
                  value: /^(https?:\/\/)?([\w\-]+\.)+[\w]{2,}(\/\S*)?$/,
                  message: 'Invalid URL!',
                },
              }}
            />
            <DisplayErrors error={handleErrors(errors?.guidelines?.[index]?.reviewLink)} />
          </div>
        </div>
        {watch(`guidelines.${index}.deliveryType.value`) === 'PRODUCTION_AND_UNIFICATION' && (
          <div className="card-form w-100 gap-4">
            <div className="w-100">
              <div className="d-flex flex w-100 gap-2 justify-content-center align-items-end">
                <div className="w-100">
                  <TextInput
                    type="text"
                    label="URL to be unified"
                    controlName={`guidelines.${index}.unificationLink`}
                    size={12}
                    placeholder="Enter the link of the content to be unified"
                    noEllipsis
                    style={{
                      ...getInputErrorStyle(!!errors?.guidelines?.[index]?.unificationLink),
                    }}
                    validation={{
                      pattern: {
                        value: /^(https?:\/\/)?([\w\-]+\.)+[\w]{2,}(\/\S*)?$/,
                        message: 'Invalid URL!',
                      },
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddUnificationLink();
                      }
                    }}
                  />
                </div>
                <Button
                  className="btn-outline"
                  style={{ width: 'fit-content' }}
                  onClick={handleAddUnificationLink}
                >
                  <i className="bx bx-plus" />
                  {translate('Add')}
                </Button>
              </div>
              <DisplayErrors
                error={handleErrors(errors?.guidelines?.[index]?.unificationLink)}
                className="pt-0 mt-0"
              />
            </div>
            <TableUnifiedLinks
              data={unificationLinks}
              onRemove={handleRemoveUnificationLink}
              onClearAll={handleClearAllUnificationLinks}
            />
          </div>
        )}
        <div className="d-flex w-100 gap-4">
          <div className="d-flex flex-column" style={{ minWidth: '30%' }}>
            <SelectInput
              label="Format"
              controlName={`guidelines.${index}.type`}
              size={4}
              required
              options={guidelineTypeOptions}
              minWidth="100%"
              styles={getSelectErrorStyle(!!errors?.guidelines?.[index]?.type)}
            />
            <DisplayErrors error={handleErrors(errors?.guidelines?.[index]?.type)} />
          </div>
          <div className="d-flex flex-column" style={{ minWidth: '30%' }}>
            <SelectInput
              label="Language"
              controlName={`guidelines.${index}.language`}
              size={3}
              required
              options={betaLanguageOptions}
              minWidth="100%"
              styles={getSelectErrorStyle(!!errors?.guidelines?.[index]?.language)}
            />
            <DisplayErrors error={handleErrors(errors?.guidelines?.[index]?.language)} />
          </div>
          <div className="d-flex flex-column w-100" style={{ minWidth: '100%' }}>
            <SpinnerInput
              controlName={`guidelines.${index}.totalSize`}
              labelTwo="Number of words"
              size={4}
              validation={{
                validate: (value) => validateTotalSize(value),
              }}
              step={100}
              required
              freeInput
              error={handleErrors(errors?.guidelines?.[index]?.totalSize)}
            />
            <DisplayErrors error={handleErrors(errors?.guidelines?.[index]?.totalSize)} />
          </div>
        </div>
        <div className="d-flex w-100 gap-4 align-items-start">
          <div className="d-flex gap-2 align-items-start m-0" style={{ width: '100%' }}>
            <div className="d-flex flex-column justify-content-end" style={{ minWidth: '50%' }}>
              <SelectInput
                label="Production month"
                controlName={`guidelines.${index}.productionDate.month`}
                size={7}
                required
                options={monthNumberLabel}
                maxWidth="100%"
                minWidth="100%"
                value={selectedMonth}
                onChange={handleSelectMonth}
                styles={getSelectErrorStyle(!!errors?.guidelines?.[index]?.productionDate?.month)}
              />
              <DisplayErrors
                error={handleErrors(errors?.guidelines?.[index]?.productionDate?.month)}
              />
            </div>
            <div className="d-flex flex-column justify-content-end" style={{ minWidth: '50%' }}>
              <div style={{ width: '1rem', height: '2.23rem' }} />
              <SelectInput
                label=""
                controlName={`guidelines.${index}.productionDate.year`}
                size={5}
                required
                options={yearOptions}
                maxWidth="100%"
                minWidth="100%"
                value={selectedYear}
                onChange={handleSelectYear}
                styles={getSelectErrorStyle(!!errors?.guidelines?.[index]?.productionDate?.year)}
                isClearable={false}
              />
              <DisplayErrors
                error={handleErrors(errors?.guidelines?.[index]?.productionDate?.year)}
              />
            </div>
          </div>
        </div>
        <div className="d-flex gap-4 w-100 justify-content-between" style={{ width: '100%' }}>
          <div className="d-flex flex-column" style={{ width: '100%', minWidth: '70%' }}>
            <TextInput
              label="Main Keyword"
              controlName={`guidelines.${index}.primaryKeyword`}
              size={7}
              placeholder="Enter a possible title for your guideline"
              noEllipsis
              required
              minWidth="100%"
              maxWidth="100%"
              style={{
                ...getInputErrorStyle(!!errors?.guidelines?.[index]?.primaryKeyword),
              }}
            />
            <DisplayErrors error={handleErrors(errors?.guidelines?.[index]?.primaryKeyword)} />
          </div>
          <div className="d-flex flex-column" style={{ width: '100%', maxWidth: '25%' }}>
            <TextInput
              label="Search Volume"
              controlName={`guidelines.${index}.searchVolume`}
              size={3}
              type="number"
              placeholder=""
              noEllipsis
              required
              minWidth="100%"
              maxWidth="100%"
              style={{
                ...getInputErrorStyle(!!errors?.guidelines?.[index]?.searchVolume),
              }}
            />
            <DisplayErrors
              error={handleErrors(errors?.guidelines?.[index]?.searchVolume)}
              style={{ display: 'flex', flexWrap: 'wrap' }}
            />
          </div>
        </div>
        <div style={{ width: '100%' }}>
          <TextInput
            label="Secondary Keywords"
            controlName={`guidelines.${index}.secondaryKeywords`}
            size={12}
            validation={{
              validate: (value) => validateSecondaryKeywordsLimit(value),
            }}
            placeholder="Enter the secondary keywords separated by commas"
            noEllipsis
            style={{
              ...getInputErrorStyle(!!errors?.guidelines?.[index]?.secondaryKeywords),
            }}
          />
          <DisplayErrors error={handleErrors(errors?.guidelines?.[index]?.secondaryKeywords)} />
          <p className="text-detail">{translate('Separate key words with commas.')}</p>
        </div>
        {canDelete && (
          <div className="full-width">
            <Button className="btn-delete" onClick={() => onDelete(index)}>
              <TrashIcon />
            </Button>
          </div>
        )}
      </div>
    </ContentCardLayout>
  );
};

ContentCard.propTypes = {
  index: PropTypes.number.isRequired,
  canDelete: PropTypes.bool,
  onDelete: PropTypes.func,
};

export default ContentCard;
